export const content = `
<p><em>Par Olivier Girodin, Direction Technique Mesh Box</em></p>

<section>
  <p><strong>Votre application est hors ligne ?</strong> Oubliez les suppositions. Voici le guide expert de <strong>dépannage site web</strong> spécialisé Node.js pour diagnostiquer les pannes silencieuses, les fuites mémoires et remettre la production en ligne en moins de 30 minutes.</p>
  
  <blockquote class="content-quote">
    <strong>Filtre de pertinence :</strong> Ce guide concerne le <strong>dépannage d'architectures applicatives</strong> (Node.js, Angular SSR, API). Si votre problème concerne un site WordPress, une erreur DNS ou un hébergement mutualisé type OVH/cPanel, ce protocole technique ne s'applique pas à votre cas.
  </blockquote>
</section>

<hr>

<section>
  <h2>🌳 Arbre de Décision Rapide (Start Here)</h2>
  <p>Identifiez votre symptôme en 5 secondes pour sauter à la bonne section :</p>
  <ul class="content-list">
    <li><strong>Cas 1 : Le site est UP mais "mouline" indéfiniment (Timeout)</strong><br>↳ Allez à <a href="#cpu-alert">Le CPU et l'Event Loop</a></li>
    <li><strong>Cas 2 : Le site crash en boucle (Error 500 / Service Unavailable)</strong><br>↳ Allez à <a href="#ram-alert">La Saturation Mémoire</a></li>
    <li><strong>Cas 3 : Je viens de faire une mise à jour et tout est cassé</strong><br>↳ Allez à <a href="#rollback">Le Rollback de Sécurité</a></li>
    <li><strong>Cas 4 : Je veux juste redémarrer proprement maintenant</strong><br>↳ Allez à <a href="#restart">Commandes de Redémarrage</a></li>
  </ul>
</section>

<hr>

<section>
  <h2>Introduction : Au-delà du "Reboot", l'Ingénierie de la Disponibilité</h2>
  <p>En 2026, le <strong>dépannage d'un site web</strong> moderne ne se résout pas en vidant un cache. Une application Node.js qui ne répond plus est souvent victime d'un blocage de l'Event Loop ou d'une fuite de mémoire native.</p>
  <p>Ce guide condense les procédures d'audit d'urgence que nous appliquons chez <strong>Mesh Box</strong>. Notre objectif n'est pas seulement de "rallumer" le serveur, mais de comprendre pourquoi il s'est éteint.</p>
</section>

<hr>

<section>
  <h2>Phase 1 : La "Golden Minute" (Diagnostic)</h2>
  <p>Si vous n'avez pas encore mis en place d'observabilité moderne (type OpenTelemetry ou Datadog), vous devez revenir aux fondamentaux. N'agissez pas à l'aveugle. Ouvrez votre terminal SSH.</p>

  <h3 id="ram-alert">1. La saturation mémoire (RSS vs Heap)</h3>
  <p>Une erreur classique en <strong>dépannage applicatif</strong> est de confondre la mémoire utilisée par le code JS (Heap) et la mémoire totale du processus (RSS).</p>
  <p><strong>Vérifiez les processus coupables :</strong></p>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash"># Affiche les processus Node triés par consommation mémoire réelle
ps aux | grep node | awk '{print $2, $4, $6, $11}' | sort -k3nr | head -n 5</code></pre>
  </div>
  <p><strong>Analyse Expert :</strong> Si la colonne %MEM est haute mais que vos logs ne montrent pas de "Fatal Error: Heap out of memory", vous avez une fuite "Native" (hors JS). C'est souvent lié à des Buffers non nettoyés ou des connexions C++ mal gérées.</p>

  <h3 id="cpu-alert">2. Le CPU : Qui bloque le thread ?</h3>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash">top -bn1 | grep "Cpu(s)"</code></pre>
  </div>
  <ul class="content-list">
    <li><strong>High User % :</strong> Votre Event Loop est bloquée par du code synchrone (boucle infinie, calcul crypto, ReDoS). Votre site web est techniquement "en ligne", mais incapable de traiter une nouvelle requête.</li>
    <li><strong>High System % :</strong> Le problème vient de l'OS (Disque saturé, trop de context switching, Garbage Collector en panique).</li>
  </ul>
</section>

<hr>

<section>
  <h2>Phase 2 : Actions de Stabilisation (Le "Quick Fix")</h2>
  <p>L'objectif ici est de rétablir le service pour les utilisateurs le plus vite possible.</p>

  <h3 id="restart">L'approche PM2 (Zero Downtime)</h3>
  <p>Si vous utilisez PM2, évitez le simple restart qui coupe les connexions en cours.</p>
  <p>La commande propre :</p>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash">pm2 reload all --update-env</code></pre>
  </div>
  <p><strong>La commande "Force Brute" (Si CPU à 100%) :</strong> Si un processus boucle à l'infini, le reload peut échouer car le process refuse de mourir. Il faut l'isoler manuellement.</p>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash">pm2 stop &lt;id&gt;</code></pre>
  </div>

  <h3 id="rollback">Le Rollback de Sécurité</h3>
  <p>Si le crash survient juste après un déploiement, le meilleur dépannage est le retour en arrière immédiat.</p>
  <blockquote class="content-quote">
    ⚠️ <strong>Attention Danger :</strong> Ne lancez la commande ci-dessous que si vous êtes certain de l'intégrité de votre package-lock.json. Une incompatibilité de versions en prod peut être fatale.
  </blockquote>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash"># Revenir au commit précédent et réinstaller proprement
git checkout &lt;commit-precedent&gt;
rm -rf node_modules
npm ci --omit=dev
pm2 reload all</code></pre>
  </div>
  <p><strong>Note :</strong> Chez Mesh Box, nous forçons l'usage de <code>npm ci</code> qui est plus strict et prédictible que <code>npm install</code> pour les environnements critiques.</p>
</section>

<hr>

<section>
  <h2>Phase 3 : Investigation Profonde & IA (Workflow 2026)</h2>
  <p>Le service est rétabli ? Parfait. Maintenant, trouvez la cause racine.</p>

  <h3>Scénario A : L'Event Loop Lag (Le site "mouline")</h3>
  <p>Le suspect : Un JSON.parse géant ou une Regex malveillante.</p>
  <p><strong>L'Outil d'Audit :</strong> Utilisez le profiler natif Node.js ou <code>clinic.js</code> pour visualiser ce qui consomme le temps CPU.</p>

  <h3>Scénario B : La Fuite Mémoire</h3>
  <p><strong>L'Action Expert :</strong> Le Snapshot à chaud. Ne redémarrez pas sans preuve !</p>
  <div class="content-code">
    <div class="code-header"><span class="lang-label">bash</span></div>
    <pre><code class="language-bash"># Génère un fichier .heapsnapshot sans tuer le processus
kill -USR2 &lt;PID_NODE&gt;</code></pre>
  </div>
  <p>Récupérez ce fichier et ouvrez-le dans Chrome DevTools > Memory. Cherchez les objets qui s'accumulent (souvent des Map globales ou des sessions utilisateurs non purgées).</p>

  <h3>💡 L'Astuce IA 2026 pour le dépannage</h3>
  <p>Ne perdez plus 2h à lire des logs bruts.</p>
  <ul class="content-list">
    <li>Extrayez les 100 dernières lignes de logs d'erreur : <code>tail -n 100 error.log</code></li>
    <li>Anonymisez les IPs/Emails.</li>
    <li>Soumettez-les à votre LLM pro avec ce prompt :</li>
  </ul>
  <blockquote class="content-quote">
    "Agis comme un expert Node.js Senior. Analyse ces logs de stacktrace. Identifie si l'erreur vient d'un module node_modules, d'un problème de connectivité DB ou du code applicatif. Suggère 2 pistes de correctifs."
  </blockquote>
</section>

<hr>

<section>
  <h2>Phase 4 : De l'Incident à l'Architecture</h2>
  <p>Vous avez éteint l'incendie. C'était du dépannage tactique.</p>
  <p>Cependant, il existe un indicateur simple : La Dette de Maintenance. Si vous ou vos équipes devez appliquer ce guide plus d'une fois par mois, le problème n'est plus le serveur, c'est l'architecture. Une application web performante doit être capable d'encaisser la charge sans intervention humaine nocturne.</p>
  <p>C'est ici que Mesh Box intervient. Nous passons du "Dépannage" à l'"Ingénierie". Nous transformons des applications fragiles en architectures résilientes (Self-Healing), capables de gérer les pics de charge et les pannes partielles sans réveiller le CTO.</p>
</section>

<hr>

<section>
  <h2>FAQ : Questions Fréquentes sur le Dépannage Node.js</h2>
  <h3>Pourquoi mon site Node.js renvoie une erreur 502 Bad Gateway ?</h3>
  <p>Cela signifie que Node.js a planté ou ne répond pas assez vite à votre proxy (Nginx/Apache). C'est souvent causé par une Event Loop bloquée par un calcul lourd.</p>

  <h3>Comment redémarrer un site Node.js sans couper les utilisateurs ?</h3>
  <p>Utilisez le "Cluster Mode" de PM2 et la commande <code>pm2 reload</code> (et non restart). Cela lance les nouveaux processus avant de tuer les anciens.</p>

  <h3>Mon serveur a assez de RAM (16Go) mais Node.js crash à 2Go, pourquoi ?</h3>
  <p>Par défaut, V8 (le moteur de Node) a une limite de Heap (souvent autour de 2Go ou 4Go sur les versions 64-bits). Vous devez augmenter cette limite via le flag <code>--max-old-space-size</code>.</p>
</section>

<div class="content-cta">
  <h3>Votre site est toujours en panne ?</h3>
  <p>Certains incidents complexes demandent une intervention immédiate par un ingénieur senior.</p>
  <a href="/contact" class="button primary">Demander une intervention d'urgence</a>
</div>

<div style="margin-top: 3rem; padding-top: 2rem; border-top: 1px solid var(--border); color: var(--muted);">
  <p><strong>Olivier Girodin</strong><br>
  <em>Lead Architect @ Mesh Box. Expert en audit de performance et stabilisation d'infrastructures critiques.</em></p>
</div>
`;
